import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { observer } from 'mobx-react';
import { IAdminxStore } from '../../../modules/stores';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { SpacerX } from '@monorepo/base/src/components/spacer/spacer';
import { sentenceCase } from 'change-case';
import styles from './edit-bidder.module.scss';
import { ErrorMsg } from '../../../modules/components/error-msg/error-msg';
import { Statuses } from '../../../modules/enums/status';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { BidderApi } from '../../../modules/apis/bidder.api';
import routes from '../../../modules/routes/index.routes';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { Nav, NavGroup, NavItem, NavPage } from '@monorepo/base/src/components/nav/nav';
import { TableOfContents } from '@monorepo/base/src/components/table-of-contents/table-of-contents';
import { ActionModal, ModalTypes } from '@monorepo/controlled/src/components/modal/modals/action-modal/action-modal';
import { useBidderAdvertiserTableOfContents } from '../../../modules/hooks/toc/bidder-adveritser.toc';
import { BidderAdvertiserForm } from '../../../modules/components/forms/bidder-advertiser/bidder-advertiser.form';
import { useParams } from 'react-router-dom';
import { useBidderAdvertiserGet } from '../../../modules/hooks/apis/use-bidder-advertiser-get';

export const EditBidderAdvertiser = observer(() => {
	const { bidderAdvertiserStore, modalsStore } = useStores<IAdminxStore>();
	const bidderAdvertiserCrudStore = bidderAdvertiserStore.getCrud();
	const isLoading = bidderAdvertiserCrudStore.getIsLoading();
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const error = bidderAdvertiserCrudStore.getHttpError();
	const advertiser = bidderAdvertiserCrudStore.getData().advertiser;
	const originAdvertiser = bidderAdvertiserCrudStore.getOriginData().advertiser;
	const isAdvertiserDirty =
		!!advertiser && !!originAdvertiser && JSON.stringify(advertiser.toObject()) !== JSON.stringify(originAdvertiser.toObject());
	const navigate = useNavigateParams();
	const { dispatchLog } = useLogs();
	const { bidderAdvertiserId } = useParams() as { bidderAdvertiserId: string };
	console.log('Rendering EditBidderAdvertiser for ID:', bidderAdvertiserId);
	useBidderAdvertiserGet({ id: bidderAdvertiserId });

	const { currentRoute } = useRoute();
	const [activeRoute, setActiveRoute] = useState<string>(currentRoute);
	const { tableOfContents, isTableOfContents, setIsTableOfContents } = useBidderAdvertiserTableOfContents();

	useEffect(() => {
		setIsTableOfContents(true);

		return () => {
			bidderAdvertiserCrudStore.reset();
		};
	}, []);

	const onEditAdvertiser = async () => {
		if (!bidderAdvertiserCrudStore.isValid()) {
			dispatchLog({
				msg: 'Form is invalid',
				type: DispatchLogsTypes.Error,
			});

			return;
		}

		setIsSaving(true);
		try {
			const advertiserToUpdate = bidderAdvertiserCrudStore.getEditFormData().toObject();
			advertiserToUpdate.balance = advertiser?.balance;
			await BidderApi.updateAdvertiser(advertiserToUpdate);
			navigate(routes.bidderAdvertisers.advertisers());
			dispatchLog({
				msg: `Advertiser ${advertiser?.advertiser_name} was successfully edited`,
				type: DispatchLogsTypes.Success,
			});
		} catch (e: any) {
			modalsStore.addModal({
				children: (
					<ActionModal type={ModalTypes.Error} primaryBtnTitle={'OK'}>
						<div className={styles.errorWrapper}>
							<h2 className={styles.title}>{`Failed to edit advertiser.`}</h2>
							{e.data?.errors?.map((error: any) => (
								<div key={error.field} className={styles.errorContent}>{`${error.field}: ${error.message}`}</div>
							))}
						</div>
					</ActionModal>
				),
			});
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<NavPage>
			<Nav onNavClick={({ to }) => to && setActiveRoute(to)} activeRoute={activeRoute}>
				<NavItem to={`${routes.bidderAdvertisers.advertisers()}`}>Advertisers</NavItem>
				<Fragment>
					{isTableOfContents && (
						<NavGroup title={'Table of contents'}>
							<TableOfContents heads={tableOfContents} />
						</NavGroup>
					)}
				</Fragment>
			</Nav>
			<Page unstyled={true}>
				<Disable is={isLoading || isSaving}>
					<Page>
						<Pagebar>
							<PagebarTitle unstyled={true}>
								<div className={styles.title}>Edit a advertiser</div>
								<SpacerX />
								<Toggle
									label={
										advertiser.advertiser_status === Statuses.Active
											? sentenceCase(Statuses.Active)
											: sentenceCase(Statuses.Paused)
									}
									onChange={status => {
										advertiser.advertiser_status = status ? Statuses.Active : Statuses.Paused;
									}}
									defaultValue={advertiser.advertiser_status === Statuses.Active}
								/>
							</PagebarTitle>
						</Pagebar>
						<BidderAdvertiserForm isEdit={true} />
					</Page>
					<BottomBar>
						{error ? <ErrorMsg error={error} /> : null}
						<BottomBarActions>
							<PrimaryButton
								onClick={() => onEditAdvertiser()}
								disabled={isLoading || isSaving || !isAdvertiserDirty}
								isRotateIcon={isLoading || isSaving}
								icon={isLoading || isSaving ? 'loading-01' : 'send-03'}>
								Save
							</PrimaryButton>
						</BottomBarActions>
					</BottomBar>
				</Disable>
			</Page>
		</NavPage>
	);
});
